import React, { useEffect, useMemo, useState } from 'react';
import InvoiceList from './InvoiceList';
import LoadStatus from './LoadStatus';
import ReturnedArticles from './ReturnedArticles';
import { post } from '../../../Services/http';
import { useInvoiceViewer } from './useInvoiceViewer';
import { useSalesInvoices } from './useSalesInvoices';
import { countedOnly } from './salesCalculations';

// Returned articles on the web credit invoices, the most returned first, with the quantity sold on the web invoices. Uses the same invoices as the sales data.
const ReturnData = () => {
    const { invoices, setInvoices, loading, progress, missingInvoices, fetchError } = useSalesInvoices();
    // Invoice list opened from an article quantity, as { title, invoices }
    const [selection, setSelection] = useState(null);
    // Product page url per article number
    const [articleUrls, setArticleUrls] = useState({});
    const { openInvoice, openOrder, refreshError, viewer } = useInvoiceViewer(setInvoices);

    const webInvoices = useMemo(() => countedOnly(invoices).filter((invoice) => invoice.isWeb), [invoices]);
    const creditInvoices = useMemo(() => webInvoices.filter((invoice) => invoice.credit), [webInvoices]);
    const soldInvoices = useMemo(() => webInvoices.filter((invoice) => !invoice.credit), [webInvoices]);
    const invoicesByNumber = useMemo(() => new Map(invoices.map((invoice) => [invoice.documentNumber, invoice])), [invoices]);

    // The article links are optional, the table works without them if the urls cannot be fetched
    useEffect(() => {
        const articleNumbers = [...new Set(creditInvoices.flatMap((invoice) => (invoice.rows || []).map((row) => row.articleNumber).filter(Boolean)))];
        if (articleNumbers.length === 0) return undefined;

        let cancelled = false;
        post('/api/sales-data/article-urls', articleNumbers)
            .then((res) => res.json())
            .then((urls) => { if (!cancelled) setArticleUrls(urls); })
            .catch(() => { });

        return () => { cancelled = true; };
    }, [creditInvoices]);

    return (
        <div style={{ margin: '2rem 0', padding: '0 1.5rem' }}>
            <LoadStatus loading={loading} progress={progress} fetchError={fetchError} missingInvoices={missingInvoices} refreshError={refreshError}>
                {selection && (
                    <InvoiceList
                        title={selection.title}
                        invoices={selection.invoices}
                        onBack={() => setSelection(null)}
                        onSelect={openInvoice}
                        onOpenOrder={openOrder}
                    />
                )}

                {/* Stays mounted behind the invoice list so that the filter, sorting and expanded analyses are kept when going back. */}
                <div style={{ display: selection ? 'none' : 'block' }}>
                    <h3 style={{ margin: '0 0 0.25rem' }}>Returer från webben</h3>
                    <div style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                        {creditInvoices.length} kreditfakturor. Sålda är antal på webbfakturor som inte är kredit. Returprocent är returnerade delat med sålda och färgas från grönt (under 1 %) till rött (30 % eller mer). Klicka på antalet för att se fakturorna bakom det, på artikelnamnet för att öppna artikelsidan och på Analysera returorsak för att visa analysen direkt under artikeln.
                    </div>
                    <ReturnedArticles
                        soldInvoices={soldInvoices}
                        creditInvoices={creditInvoices}
                        articleUrls={articleUrls}
                        invoicesByNumber={invoicesByNumber}
                        onShowInvoices={setSelection}
                    />
                </div>

                {viewer}
            </LoadStatus>
        </div>
    );
};

export default ReturnData;
